import {isNil} from 'ramda';

import {getCompositionPlacement} from './getCompositionPlacement';

// The result is a photo, so JPEG keeps the data URL several times smaller than PNG.
const PREVIEW_JPEG_QUALITY = 0.9;

const loadImage = (url: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = document.createElement('img');
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('The watermark preview image did not load'));
    image.src = url;
  });

/**
 * Draws a rendered watermark over the preview photo and returns the result as a data URL. The
 * result keeps the photo size and the BE placement of a saved composition, so on the settings
 * sample photo it matches the saved settings preview.
 */
export const composeOnPreviewPhoto = async (photoUrl: string, watermarkUrl: string) => {
  const [photo, watermark] = await Promise.all([loadImage(photoUrl), loadImage(watermarkUrl)]);

  const canvas = document.createElement('canvas');
  canvas.width = photo.naturalWidth;
  canvas.height = photo.naturalHeight;
  const context = canvas.getContext('2d');
  if (isNil(context)) {
    throw new Error('The browser does not support a 2D canvas');
  }

  const placement = getCompositionPlacement(
    {width: canvas.width, height: canvas.height},
    {width: watermark.naturalWidth, height: watermark.naturalHeight}
  );

  context.drawImage(photo, 0, 0);
  context.drawImage(watermark, placement.x, placement.y, placement.width, placement.height);

  return canvas.toDataURL('image/jpeg', PREVIEW_JPEG_QUALITY);
};
